<template>
  <div>
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:justify-between gap-3 mb-6">
      <div class="flex items-center gap-1.5">
        <div class="analytics-date-pill">
          <AnalyticsDateRangePicker v-model="dateRange" />
        </div>
        <span v-if="previousPeriodStartDate" class="text-xs font-medium text-gray-400">vs</span>
        <Selector
          v-if="previousPeriodStartDate"
          id="comparison-type-selector"
          name="comparisonType"
          size="small"
          v-model="comparisonType"
          :options="comparisonTypeOptions"
        />
      </div>
      <div class="self-start w-72">
        <AnalyticsSelector @update-selections="handleSelectionsUpdate" />
      </div>
    </div>

    <!-- Main content -->
    <div v-if="isPresent(generalAnalytics)" class="flex flex-col gap-4">
      <!-- Charts + KPI cards -->
      <div class="bg-white p-6 rounded-lg shadow-lg">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SalesChart :analytics="generalAnalytics" :chart-view="chartView" :comparison-type="comparisonType" />
          <TurnoverChart :analytics="generalAnalytics" :chart-view="chartView" :comparison-type="comparisonType" />
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 border-t border-gray-100 pt-4">
          <DataCardWithVariation
            v-if="generalData?.todayContracts"
            title="Contrats aujourd'hui"
            tooltipText="Nombre de contrats vendus aujourd'hui."
            :data="generalData.todayContracts"
          />
          <DataCardWithVariation
            v-if="generalData?.premium"
            title="Primes"
            tooltipText="Montant total des primes TTC générées par les contrats."
            :data="generalData.premium"
            :comparisonType="comparisonType"
          />
          <DataCardWithVariation
            v-if="generalData?.storeCommission"
            title="Rémunération marchand"
            tooltipText="Montant total des commissions perçues par le marchand."
            :data="generalData.storeCommission"
            :comparisonType="comparisonType"
          />
          <DataCardWithVariation
            v-if="generalData"
            title="Taux de résiliation"
            tooltipText="Pourcentage de contrats résiliés par rapport au total des contrats vendus pendant la période sélectionnée."
            :data="generalData.resiliationRate"
            :comparisonType="comparisonType"
          />
        </div>
      </div>

      <!-- Ranking tables -->
      <div
        v-if="storesRankingVisible || sellersRankingVisible"
        :class="storesRankingVisible && sellersRankingVisible ? 'grid grid-cols-1 lg:grid-cols-2 gap-4' : ''"
      >
        <div v-if="storesRankingVisible" class="bg-white px-6 py-4 rounded-lg shadow-lg">
          <p class="text-sm font-medium text-gray-500 mb-2">Meilleurs points de vente</p>
          <StoreRankingTable :data="topStoresRanking" />
        </div>
        <div v-if="sellersRankingVisible" class="bg-white px-6 py-4 rounded-lg shadow-lg">
          <p class="text-sm font-medium text-gray-500 mb-2">Meilleurs vendeurs</p>
          <SellerRankingTable :data="generalAnalytics.sales.sellersRanking" />
        </div>
      </div>

      <!-- Pie charts -->
      <AnalyticsSalesRepartition v-if="generalAnalytics.sales?.verticalRepartition" :analytics="generalAnalytics" />

      <!-- Claims -->
      <AnalyticsClaimCards v-if="generalAnalytics.claims" :analytics="generalAnalytics" />
    </div>
  </div>
</template>

<script>
import AnalyticsSelector from "@/components/analytics/AnalyticsSelector";
import SalesChart from "@/components/analytics/SalesChart";
import TurnoverChart from "@/components/analytics/TurnoverChart";
import {
  AnalyticsSalesRepartition,
  AnalyticsClaimCards,
  DataCardWithVariation,
  AnalyticsDateRangePicker,
  Selector,
  StoreRankingTable,
  SellerRankingTable,
  isPresent,
  currentMonthRange,
  inputDateFormat,
  currentWeek,
  currentYear,
  currentMonth,
} from "@estaly/ui";
import { differenceInMonths } from "date-fns";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  components: {
    AnalyticsSelector,
    SalesChart,
    TurnoverChart,
    AnalyticsSalesRepartition,
    AnalyticsClaimCards,
    DataCardWithVariation,
    AnalyticsDateRangePicker,
    Selector,
    StoreRankingTable,
    SellerRankingTable,
  },

  data() {
    return {
      selectedOrganizationId: null,
      selectedStoreIds: [],
      dateRange: currentMonthRange().map((date) => inputDateFormat(date)),
      comparisonType: "previousMonth",
      chartView: "daily",
      comparisonTypeOptions: [
        { value: "previousMonth", label: "Mois précédent" },
        { value: "previousPeriod", label: "Période précédente" },
        { value: "previousWeek", label: "Semaine précédente" },
        { value: "previousYear", label: "Année précédente" },
      ],
    };
  },

  computed: {
    ...mapState("analytics", ["generalAnalytics"]),
    ...mapGetters("analytics", ["generalData", "previousPeriodStartDate"]),

    storesRankingVisible() {
      return (this.generalAnalytics?.sales?.storesRanking?.length ?? 0) >= 3;
    },

    topStoresRanking() {
      return (this.generalAnalytics?.sales?.storesRanking ?? []).slice(0, 5);
    },

    sellersRankingVisible() {
      return isPresent(this.generalAnalytics?.sales?.sellersRanking);
    },

    apiParams() {
      return {
        organizationId: this.selectedOrganizationId,
        stores: this.selectedStoreIds,
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        comparisonType: this.comparisonType,
      };
    },
  },

  watch: {
    comparisonType() {
      this.fetchAnalytics();
    },
    dateRange(newValue) {
      this.autoSwitchChartView(newValue);
      const defaultComparison = this.getDefaultComparisonTypeForRange(newValue);
      if (defaultComparison !== this.comparisonType) {
        this.comparisonType = defaultComparison;
      } else {
        this.fetchAnalytics();
      }
    },
  },

  mounted() {
    this.fetchAnalytics();
  },

  methods: {
    ...mapActions("analytics", ["fetchGeneralAnalytics"]),
    isPresent,

    getDefaultComparisonTypeForRange(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));

      if (currentWeek(startDate, endDate)) return "previousWeek";
      if (currentYear(startDate, endDate)) return "previousYear";
      if (currentMonth(startDate, endDate)) return "previousMonth";

      return "previousPeriod";
    },

    autoSwitchChartView(dateRange) {
      const [startDate, endDate] = dateRange.map((d) => new Date(d));
      const monthsDifference = differenceInMonths(endDate, startDate);
      this.chartView = monthsDifference > 2 ? "monthly" : "daily";
    },

    handleSelectionsUpdate(selections) {
      if (selections.organizations.length > 0) {
        this.selectedOrganizationId = selections.organizations[0].id;
        this.selectedStoreIds = [];
      } else if (selections.stores.length > 0) {
        this.selectedStoreIds = selections.stores.map((store) => store.id);
        this.selectedOrganizationId = null;
      } else {
        this.selectedOrganizationId = null;
        this.selectedStoreIds = [];
      }

      this.fetchAnalytics();
    },

    fetchAnalytics() {
      this.fetchGeneralAnalytics(this.apiParams);
    },
  },
};
</script>

<style scoped>
.analytics-date-pill :deep(.dp__input) {
  font-size: 12px !important;
  padding: 5px 10px !important;
  padding-right: 24px !important;
  height: auto !important;
  min-height: unset !important;
  line-height: 1.4 !important;
  border-radius: 0.5rem;
}

.analytics-date-pill :deep(.dp__clear_icon) {
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
